import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Badge,
  Icon,
  Portal,
  HStack,
  Image,
  Center
} from '@chakra-ui/react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';

// Company Image Component with fallback
const CompanyImage: React.FC<{ src?: string; alt?: string; size?: string | number }> = ({ 
  src, 
  alt = "Company Logo", 
  size = 4 
}) => {
  const [imageError, setImageError] = useState(false);
  
  if (!src || src === 'placeholder' || imageError) {
    return (
      <Center 
        boxSize={size} 
        bg="gray.100" 
        borderRadius="sm"
        border="1px solid"
        borderColor="gray.200"
      >
        <Icon as={MdBusiness} boxSize={3} color="gray.400" />
      </Center>
    );
  }
  
  return (
    <Image 
      src={src} 
      alt={alt} 
      boxSize={size} 
      borderRadius="sm"
      border="1px solid"
      borderColor="gray.200"
      onError={() => setImageError(true)}
      fallback={
        <Center 
          boxSize={size} 
          bg="gray.100" 
          borderRadius="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          <Icon as={MdBusiness} boxSize={3} color="gray.400" />
        </Center>
      }
    />
  );
};

// Generic option interface
export interface DropdownOption {
  id: string;
  label: string;
  value?: any;
  icon?: React.ComponentType;
  iconSrc?: string;
  color?: string;
  disabled?: boolean;
  subtitle?: string;
  badge?: string;
  [key: string]: any; // Allow additional properties
}

interface CustomDropdownProps {
  options: DropdownOption[];
  selectedId: string;
  onSelectionChange: (optionId: string, option: DropdownOption) => void;
  placeholder?: string;
  maxWidth?: string | Record<string, string>;
  disabled?: boolean;
  showBadgeForSingle?: boolean;
  badgeProps?: Record<string, any>;
  triggerProps?: Record<string, any>;
  dropdownProps?: Record<string, any>;
  showIcons?: boolean;
  showCheck?: boolean;
  searchable?: boolean;
  emptyMessage?: string;
  renderOption?: (option: DropdownOption, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (selectedOption: DropdownOption | undefined, isOpen: boolean) => React.ReactNode;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  selectedId,
  onSelectionChange,
  placeholder = 'Select an option',
  maxWidth = { base: "100%", md: "250px" },
  disabled = false,
  showBadgeForSingle = true,
  badgeProps = {},
  triggerProps = {},
  dropdownProps = {},
  showIcons = true,
  showCheck = true,
  searchable = false,
  emptyMessage = 'No options available',
  renderOption,
  renderTrigger
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [dropdownPosition, setDropdownPosition] = React.useState<{ 
    top: number; 
    left: number; 
    width: number; 
    placement: 'top' | 'bottom'; 
  }>({ top: 0, left: 0, width: 0, placement: 'bottom' });
  
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const selectedOption = options.find(option => option.id === selectedId);
  
  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.subtitle?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Calculate dropdown position with smart placement
  const updateDropdownPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const viewportHeight = window.innerHeight;

      // Get actual dropdown height if it's rendered, otherwise estimate
      let dropdownHeight = 300; // default max height
      if (dropdownRef.current) {
        const actualHeight = dropdownRef.current.scrollHeight;
        dropdownHeight = Math.min(actualHeight, 300);
      } else {
        // Estimate dropdown height based on content
        dropdownHeight = Math.min(
          300, // max height from your CSS
          (searchable ? 50 : 0) + // search input height
          (filteredOptions.length * 50) + // estimated item height (50px per item)
          20 // padding
        );
      }

      const spaceBelow = viewportHeight - rect.bottom;

      // Smart placement logic - be more aggressive about opening upward
      // If dropdown would be cut off at bottom, place above
      const shouldPlaceAbove = spaceBelow < dropdownHeight;

      // Calculate final position
      let finalTop;
      let actualPlacement: 'top' | 'bottom' = 'bottom';
      
      if (shouldPlaceAbove) {
        // Place above: position so dropdown bottom aligns with trigger top (viewport coordinates)
        finalTop = rect.top - dropdownHeight - 4;
        // Ensure it doesn't go off-screen at the top
        finalTop = Math.max(4, finalTop);
        actualPlacement = 'top';
      } else {
        // Place below: trigger bottom + gap (viewport coordinates)
        finalTop = rect.bottom + 4;
        // Check if the dropdown would go off-screen at the bottom
        const dropdownBottom = finalTop + dropdownHeight;
        const maxAllowedBottom = viewportHeight - 10; // 10px margin from bottom
        
        if (dropdownBottom > maxAllowedBottom) {
          // If it would go off-screen, force placement above instead
          finalTop = rect.top - dropdownHeight - 4;
          finalTop = Math.max(4, finalTop);
          actualPlacement = 'top';
        } else {
          actualPlacement = 'bottom';
        }
      }

      setDropdownPosition({
        top: finalTop,
        left: rect.left + scrollX,
        width: rect.width,
        placement: actualPlacement
      });
    }
  }, [filteredOptions.length, searchable]);

  // Handle click outside and escape key
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const closeDropdown = () => {
      setIsOpen(false);
      setSearchTerm('');
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', closeDropdown);
      window.addEventListener('resize', closeDropdown);
      
      // Focus search input if searchable
      if (searchable && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('scroll', closeDropdown);
        window.removeEventListener('resize', closeDropdown);
      };
    }
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (disabled) return;
    
    if (!isOpen) {
      setIsOpen(true);
      setSearchTerm('');
      // Update position after DOM is updated
      setTimeout(() => {
        updateDropdownPosition();
      }, 10);
    } else {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleOptionSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    
    onSelectionChange(option.id, option);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Render single option badge
  if (options.length <= 1 && showBadgeForSingle) {
    return selectedOption?.label ? (
      <Badge
        fontSize={{ base: "sm", md: "md" }}
        px={4}
        py={2}
        borderRadius="12px"
        fontWeight="600"
        bg="rgba(59, 130, 246, 0.1)"
        color="#1d4ed8"
        border="1px solid rgba(59, 130, 246, 0.2)"
        sx={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
        {...badgeProps}
      >
        {showIcons && selectedOption.icon && (
          <Icon as={selectedOption.icon} mr={2} boxSize={4} />
        )}
        {showIcons && selectedOption.iconSrc && (
          selectedOption.iconSrc.match(/^https?:|^data:|^blob:/)
            ? <Image src={selectedOption.iconSrc} alt="" boxSize={4} mr={2} />
            : <span style={{ fontSize: '1.2em', marginRight: 8 }}>{selectedOption.iconSrc}</span>
        )}
        {selectedOption.label}
      </Badge>
    ) : null;
  }

  // Default option renderer
  const defaultRenderOption = (option: DropdownOption, isSelected: boolean) => (
    <HStack spacing={3} w="100%" align="center">
      {showIcons && (option.icon || option.iconSrc) && (
        <Box flexShrink={0}>
          {option.icon && (
            <Icon 
              as={option.icon} 
              boxSize={4} 
              color={option.color || (isSelected ? "#1d4ed8" : "#6b7280")}
            />
          )}
          {option.iconSrc && (
            option.iconSrc.match(/^https?:|^data:|^blob:/) || option.iconSrc === 'placeholder'
              ? <CompanyImage src={option.iconSrc} alt={`${option.label} Logo`} size={4} />
              : <span style={{ fontSize: '1.2em', marginRight: 8 }}>{option.iconSrc}</span>
          )}
        </Box>
      )}
      <VStack spacing={0} align="start" flex={1} minW={0}>
        <HStack spacing={2} w="100%" align="center">
          <Text 
            isTruncated 
            flex={1}
            fontWeight={isSelected ? "600" : "500"}
          >
            {option.label}
          </Text>
          {option.badge && (
            <Badge size="sm" colorScheme="gray">
              {option.badge}
            </Badge>
          )}
        </HStack>
        {option.subtitle && (
          <Text 
            fontSize="xs" 
            color="gray.500" 
            isTruncated 
            w="100%"
          >
            {option.subtitle}
          </Text>
        )}
      </VStack>
      {showCheck && isSelected && (
        <Icon
          as={FiCheck}
          boxSize={4}
          color="#1d4ed8"
          flexShrink={0}
        />
      )}
    </HStack>
  );

  // Default trigger renderer
  const defaultRenderTrigger = (selectedOpt: DropdownOption | undefined, isOpenState: boolean) => (
    <HStack spacing={2} w="100%" align="center">
      {showIcons && selectedOpt && (selectedOpt.icon || selectedOpt.iconSrc) && (
        <Box flexShrink={0}>
          {selectedOpt.icon && (
            <Icon as={selectedOpt.icon} boxSize={4} color={selectedOpt.color || "#6b7280"} />
          )}
          {selectedOpt.iconSrc && (
            selectedOpt.iconSrc.match(/^https?:|^data:|^blob:/) || selectedOpt.iconSrc === 'placeholder'
              ? <CompanyImage src={selectedOpt.iconSrc} alt={`${selectedOpt.label} Logo`} size={4} />
              : <span style={{ fontSize: '1.2em', marginRight: 8 }}>{selectedOpt.iconSrc}</span>
          )}
        </Box>
      )}
      <Text isTruncated flex={1} textAlign="left">
        {selectedOpt?.label || placeholder}
      </Text>
      <Icon
        as={FiChevronDown}
        boxSize={4}
        color="#6b7280"
        flexShrink={0}
        sx={{
          transform: isOpenState ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </HStack>
  );

  return (
    <>
      {/* Dropdown Trigger */}
      <Button
        ref={triggerRef}
        onClick={handleToggle}
        variant="unstyled"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        maxW={maxWidth}
        h="auto"
        px={4}
        py={3}
        bg="rgba(248, 250, 252, 0.8)"
        border="1px solid rgba(226, 232, 240, 0.6)"
        borderRadius="12px"
        fontWeight="600"
        color="#374151"
        fontSize={{ base: "sm", md: "md" }}
        cursor={disabled ? "not-allowed" : "pointer"}
        userSelect="none"
        opacity={disabled ? 0.6 : 1}
        sx={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
          
          '&:hover': !disabled ? {
            bg: 'rgba(241, 245, 249, 0.9)',
            borderColor: 'rgba(203, 213, 225, 0.8)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          } : {},
          
          '&:active': !disabled ? {
            bg: 'rgba(241, 245, 249, 0.9)',
            transform: 'translateY(0)',
          } : {},
          
          '&:focus': !disabled ? {
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
          } : {}
        }}
        {...triggerProps}
      >
        {renderTrigger ? renderTrigger(selectedOption, isOpen) : defaultRenderTrigger(selectedOption, isOpen)}
      </Button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            top={`${dropdownPosition.top}px`}
            left={`${dropdownPosition.left}px`}
            width={`${dropdownPosition.width}px`}
            zIndex={9999}
            bg="rgba(255, 255, 255, 0.95)"
            border="1px solid rgba(226, 232, 240, 0.6)"
            borderRadius="12px"
            overflow="hidden"
            maxH="300px"
            sx={{
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: dropdownPosition.placement === 'top' 
                ? '0 -20px 25px -5px rgba(0, 0, 0, 0.1), 0 -10px 10px -5px rgba(0, 0, 0, 0.04)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            {...dropdownProps}
          >
            <VStack spacing={0} align="stretch">
              {/* Search Input */}
              {searchable && (
                <Box p={3} borderBottom="1px solid rgba(241, 245, 249, 0.8)">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid rgba(226, 232, 240, 0.6)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      background: 'rgba(248, 250, 252, 0.5)',
                    }}
                  />
                </Box>
              )}

              {/* Options */}
              <Box maxH="250px" overflowY="auto" sx={{
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { bg: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  bg: 'rgba(156, 163, 175, 0.5)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  bg: 'rgba(156, 163, 175, 0.7)',
                },
              }}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <Button
                      key={option.id}
                      onClick={() => handleOptionSelect(option)}
                      variant="unstyled"
                      display="flex"
                      alignItems="center"
                      w="100%"
                      h="auto"
                      px={4}
                      py={3}
                      bg="transparent"
                      borderRadius={0}
                      borderTop={index > 0 ? "1px solid rgba(241, 245, 249, 0.8)" : "none"}
                      fontWeight={selectedId === option.id ? "600" : "500"}
                      color={
                        option.disabled ? "#9ca3af" :
                        selectedId === option.id ? "#1d4ed8" : "#374151"
                      }
                      fontSize={{ base: "sm", md: "md" }}
                      textAlign="left"
                      cursor={option.disabled ? "not-allowed" : "pointer"}
                      userSelect="none"
                      opacity={option.disabled ? 0.5 : 1}
                      sx={{
                        WebkitTapHighlightColor: 'transparent',
                        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                        outline: 'none',
                        
                        '&:hover': !option.disabled ? {
                          bg: 'rgba(59, 130, 246, 0.05)',
                          color: '#1d4ed8',
                        } : {},
                        
                        '&:active': !option.disabled ? {
                          bg: 'rgba(59, 130, 246, 0.1)',
                        } : {}
                      }}
                    >
                      {renderOption ? renderOption(option, selectedId === option.id) : defaultRenderOption(option, selectedId === option.id)}
                    </Button>
                  ))
                ) : (
                  <Box p={4} textAlign="center">
                    <Text color="gray.500" fontSize="sm">
                      {emptyMessage}
                    </Text>
                  </Box>
                )}
              </Box>
            </VStack>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default CustomDropdown;